import { useState, useEffect } from 'react';
import { Check, RotateCcw } from 'lucide-react';
import { MobileNumberStepper } from './MobileNumberStepper';
import { cn } from '@/lib/utils';

interface PreviousSet {
  weight: number | null;
  reps: number;
}

interface SetLoggerProps {
  exerciseName: string;
  setNumber: number;
  previousSet?: PreviousSet;
  initialWeight?: number | null;
  initialReps?: number;
  onSave: (weight: number | null, reps: number) => void;
  onCancel?: () => void;
  isSaving?: boolean;
  className?: string;
}

export function SetLogger({
  exerciseName,
  setNumber,
  previousSet,
  initialWeight = null,
  initialReps = 1,
  onSave,
  onCancel,
  isSaving = false,
  className,
}: SetLoggerProps) {
  const [weight, setWeight] = useState<number | null>(initialWeight);
  const [reps, setReps] = useState<number>(initialReps);
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false);

  // Reset when exercise changes
  useEffect(() => {
    setWeight(initialWeight);
    setReps(initialReps);
  }, [initialWeight, initialReps]);

  const handleCopyPrevious = () => {
    if (previousSet) {
      setWeight(previousSet.weight);
      setReps(previousSet.reps);
      setShowCopiedFeedback(true);
      setTimeout(() => setShowCopiedFeedback(false), 1500);
    }
  };

  const handleSave = () => {
    onSave(weight, reps);
  };

  const formatPreviousSet = () => {
    if (!previousSet) return null;
    const weightStr = previousSet.weight !== null ? `${previousSet.weight} kg` : 'kehonpaino';
    return `${weightStr} × ${previousSet.reps}`;
  };

  return (
    <div className={cn('bg-white dark:bg-gray-800 rounded-2xl shadow-lg overflow-hidden', className)}>
      {/* Header */}
      <div className="bg-gray-50 dark:bg-gray-900 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-gray-900 dark:text-white">
              {exerciseName}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Sarja {setNumber}
            </p>
          </div>
          {onCancel && (
            <button
              type="button"
              onClick={onCancel}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 p-2 rounded-lg focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500"
              aria-label="Peruuta"
            >
              <RotateCcw className="h-5 w-5" aria-hidden="true" />
            </button>
          )}
        </div>
      </div>

      {/* Previous set hint */}
      {previousSet && (
        <button
          type="button"
          onClick={handleCopyPrevious}
          aria-label={`Kopioi edellinen sarja: ${formatPreviousSet()}`}
          className={cn(
            'w-full px-4 py-3 text-center transition-all touch-manipulation',
            'border-b border-gray-100 dark:border-gray-700',
            'hover:bg-gray-50 dark:hover:bg-gray-750',
            'active:bg-gray-100 dark:active:bg-gray-700',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
            showCopiedFeedback
              ? 'bg-green-50 dark:bg-green-900/20 text-green-600 dark:text-green-400'
              : 'text-gray-600 dark:text-gray-300'
          )}
        >
          {showCopiedFeedback ? (
            <span className="flex items-center justify-center gap-2">
              <Check className="h-4 w-4" aria-hidden="true" />
              Kopioitu!
            </span>
          ) : (
            <span>
              Edellinen: <span className="font-medium text-gray-700 dark:text-gray-300">{formatPreviousSet()}</span>
              <span className="text-xs ml-2">(nappauta kopioidaksesi)</span>
            </span>
          )}
        </button>
      )}

      {/* Input fields */}
      <div className="p-4 space-y-6">
        {/* Weight input */}
        <MobileNumberStepper
          value={weight}
          onChange={setWeight}
          min={0}
          max={500}
          step={2.5}
          label="Paino"
          unit="kg"
          size="large"
        />

        {/* Reps input */}
        <MobileNumberStepper
          value={reps}
          onChange={(val) => setReps(val ?? 1)}
          min={1}
          max={500}
          step={1}
          label="Toistot"
          size="large"
        />
      </div>

      {/* Save button */}
      <div className="p-4 pt-0">
        <button
          type="button"
          onClick={handleSave}
          disabled={isSaving}
          className={cn(
            'w-full flex items-center justify-center gap-2',
            'min-h-[52px] px-6 text-lg font-semibold',
            'bg-green-600 text-white rounded-xl',
            'transition-all duration-200 touch-manipulation',
            'hover:bg-green-700',
            'active:scale-[0.98] active:bg-green-800',
            'disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2'
          )}
        >
          {isSaving ? (
            <span className="animate-pulse">Tallennetaan...</span>
          ) : (
            <>
              <Check className="h-5 w-5" aria-hidden="true" />
              Tallenna sarja
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// Compact version for inline editing in list
interface SetRowProps {
  setNumber: number;
  weight: number | null;
  reps: number;
  previousSet?: PreviousSet;
  onWeightChange: (value: number | null) => void;
  onRepsChange: (value: number) => void;
  onDelete: () => void;
  onDuplicate: () => void;
}

export function SetRow({
  setNumber,
  weight,
  reps,
  previousSet,
  onWeightChange,
  onRepsChange,
  onDelete,
  onDuplicate,
}: SetRowProps) {
  const [showCopied, setShowCopied] = useState(false);

  const handleCopyPrevious = () => {
    if (previousSet) {
      onWeightChange(previousSet.weight);
      onRepsChange(previousSet.reps);
      setShowCopied(true);
      setTimeout(() => setShowCopied(false), 1000);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-800/50 rounded-xl p-4 space-y-3">
      {/* Header with set number and actions */}
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
          Sarja {setNumber}
        </span>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={onDuplicate}
            aria-label={`Kopioi sarja ${setNumber}`}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-lg',
              'text-primary-600 dark:text-primary-400',
              'hover:bg-primary-50 dark:hover:bg-primary-900/30',
              'active:bg-primary-100 dark:active:bg-primary-900/50',
              'transition-colors touch-manipulation',
              'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500'
            )}
          >
            Kopioi
          </button>
          <button
            type="button"
            onClick={() => {
              if (window.confirm('Poistetaanko sarja?')) {
                onDelete();
              }
            }}
            aria-label={`Poista sarja ${setNumber}`}
            className={cn(
              'px-3 py-1.5 text-xs font-medium rounded-lg',
              'text-red-600 dark:text-red-400',
              'hover:bg-red-50 dark:hover:bg-red-900/30',
              'active:bg-red-100 dark:active:bg-red-900/50',
              'transition-colors touch-manipulation',
              'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-red-500'
            )}
          >
            Poista
          </button>
        </div>
      </div>

      {/* Previous set hint */}
      {previousSet && (
        <button
          type="button"
          onClick={handleCopyPrevious}
          aria-label={`Kopioi edellinen sarja: ${previousSet.weight ?? 0} kg × ${previousSet.reps}`}
          className={cn(
            'w-full py-2 px-3 rounded-lg text-sm text-center',
            'transition-all touch-manipulation',
            'focus:outline-hidden focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary-500',
            showCopied
              ? 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400'
              : 'bg-gray-100 dark:bg-gray-700/50 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700 active:bg-gray-300 dark:active:bg-gray-600'
          )}
        >
          {showCopied ? (
            <span className="flex items-center justify-center gap-1">
              <Check className="h-3 w-3" aria-hidden="true" /> Kopioitu!
            </span>
          ) : (
            <>
              Edellinen: {previousSet.weight ?? 0} kg × {previousSet.reps}
            </>
          )}
        </button>
      )}

      {/* Inputs */}
      <div className="grid grid-cols-2 gap-4">
        <MobileNumberStepper
          value={weight}
          onChange={onWeightChange}
          min={0}
          max={500}
          step={2.5}
          label="Paino"
          unit="kg"
        />
        <MobileNumberStepper
          value={reps}
          onChange={(val) => onRepsChange(val ?? 1)}
          min={1}
          max={500}
          step={1}
          label="Toistot"
        />
      </div>
    </div>
  );
}
